import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useAppUser from '../../../hooks/useAppUser';
import useNotify from '../../../hooks/useNotify';
import { PremiumDbsEvents } from './AgencySettingsDbs.types';
import useRequestPremiumDbsEditorLink from './useRequestPremiumDbsEditorLink';
import useFetchPremiumDbsSiteStatus from './useFetchPremiumDbsSiteStatus';

function getLegacyPageIframe() {
  return document.querySelector(
    'iframe#legacy-page-frame',
  ) as HTMLIFrameElement;
}

function setPremiumDbsSiteStatus(statusPayload: { connected: boolean }) {
  getLegacyPageIframe()
    ?.contentWindow // @ts-expect-error
    ?.setPremiumDbsSiteStatus(statusPayload);
}

function togglePremiumDbsEditorButton(togglePayload: { disabled: boolean }) {
  getLegacyPageIframe()
    ?.contentWindow // @ts-expect-error
    ?.togglePremiumDbsEditorButton(togglePayload);
}

let editorWindowRef: Window | null;

const useHandlePremiumDbsEvents = () => {
  const { agencyUid, user } = useAppUser();
  const { notifyError } = useNotify();
  const { refetch: fetchSiteStatus } = useFetchPremiumDbsSiteStatus({
    agencyUid: agencyUid!,
  });
  const { mutateAsync: requestEditorLink } = useRequestPremiumDbsEditorLink();
  const { t } = useTranslation();

  return useCallback(
    ({
      focusPremiumDbsEditorWindow,
      getPremiumDbsSiteStatus,
      openPremiumDbsEditor,
    }: PremiumDbsEvents) => {
      if (focusPremiumDbsEditorWindow) {
        if (editorWindowRef && !editorWindowRef.closed) {
          editorWindowRef.focus();
        }
      }

      if (getPremiumDbsSiteStatus) {
        try {
          fetchSiteStatus().then(({ data }) => {
            setPremiumDbsSiteStatus({ connected: !!data });
          });
        } catch (error) {
          notifyError(t('pageAgencySettings.dbs.premium.error.siteStatus'));
          setPremiumDbsSiteStatus({ connected: false });
        }
      }

      if (openPremiumDbsEditor) {
        try {
          requestEditorLink({
            agencyUid: agencyUid!,
            employeeEmail: user?.email,
            employeeFirstName: user?.firstName,
            employeeLastName: user?.lastName,
          }).then(({ data: { ssoUrl } }) => {
            if (ssoUrl) {
              editorWindowRef = window.open(ssoUrl, 'hf-premium-dbs-editor');
            } else {
              notifyError(t('pageAgencySettings.dbs.premium.error.openEditor'));
            }

            togglePremiumDbsEditorButton({ disabled: false });
          });
        } catch (error) {
          notifyError(t('pageAgencySettings.dbs.premium.error.openEditor'));
          togglePremiumDbsEditorButton({ disabled: false });
        }
      }
    },
    [agencyUid, user],
  );
};

export default useHandlePremiumDbsEvents;
